import { Injectable, inject, signal } from '@angular/core';
// Firebase is loaded on demand inside enable() — a static import would put the
// whole SDK in the initial bundle on every page, but it is only needed once
// the user opts in to push.
import type { FirebaseApp } from 'firebase/app';
import type { Messaging } from 'firebase/messaging';
import { environment } from '../../environments/environment';
import { SupabaseService } from './supabase.service';

export type PushState =
  | 'unsupported' // browser has no push APIs (or iOS Safari not installed)
  | 'needs-install' // iOS: web push only works from the installed PWA
  | 'idle' // supported, not asked yet
  | 'enabled' // token registered with the backend
  | 'denied' // user blocked notifications
  | 'error';

/**
 * Web push (FCM) for the platform PWA — the channel that lets Actively reach
 * an admin who is NOT looking at the app (new visit request, new order).
 *
 * enable(): registers /firebase-messaging-sw.js, asks Notification permission,
 * mints the FCM registration token (VAPID web-push certificate, same Firebase
 * project as the Booking app) and subscribes it via the existing
 * pushNotificationsSubscription edge function — one row per admin club, so
 * club-scoped sends keep working. iOS Safari only allows web push from an
 * installed (Add to Home Screen) app, which is why the prompt pushes install.
 */
@Injectable({ providedIn: 'root' })
export class PushService {
  private readonly supabase = inject(SupabaseService);

  private app?: FirebaseApp;
  private messaging?: Messaging;

  private readonly _state = signal<PushState>(this.detect());
  readonly state = this._state.asReadonly();

  /** True when running as an installed app (PWA standalone / iOS homescreen). */
  get isStandalone(): boolean {
    return (
      window.matchMedia?.('(display-mode: standalone)')?.matches ||
      (navigator as any).standalone === true
    );
  }

  private detect(): PushState {
    if (typeof window === 'undefined') return 'unsupported';
    const hasApis =
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window;
    if (!hasApis) {
      // iOS Safari exposes the APIs only inside an installed PWA (16.4+).
      const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
      return ios && !this.isStandalone ? 'needs-install' : 'unsupported';
    }
    if (Notification.permission === 'denied') return 'denied';
    if (
      Notification.permission === 'granted' &&
      localStorage.getItem('actively_fcm_token')
    ) {
      return 'enabled';
    }
    return 'idle';
  }

  /** Re-evaluate (e.g. after the PWA gets installed). */
  refresh(): void {
    this._state.set(this.detect());
  }

  /**
   * Full opt-in flow. Returns the resulting state; all failures are absorbed
   * into the state signal so callers just render it.
   */
  async enable(): Promise<PushState> {
    try {
      if (this._state() === 'unsupported' || this._state() === 'needs-install') {
        return this._state();
      }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        this._state.set(permission === 'denied' ? 'denied' : 'idle');
        return this._state();
      }

      const registration = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js'
      );
      const [{ initializeApp }, { getMessaging, getToken }] = await Promise.all([
        import('firebase/app'),
        import('firebase/messaging'),
      ]);
      this.app ??= initializeApp(environment.firebase);
      this.messaging ??= getMessaging(this.app);
      const fcmToken = await getToken(this.messaging, {
        vapidKey: environment.vapidPublicKey,
        serviceWorkerRegistration: registration,
      });
      if (!fcmToken) throw new Error('empty FCM token');

      // One subscription row per ADMIN club (club-scoped sends on the backend).
      const user = await this.supabase.getCurrentUser();
      if (!user) throw new Error('not signed in');
      const clubs = await this.supabase.getUserClubs(user.id);
      const adminClubs = (clubs ?? []).filter(
        (c) => c.role === 'admin' || c.role === 'super_admin'
      );
      if (!adminClubs.length) throw new Error('no admin clubs');
      for (const c of adminClubs) {
        const { error } = await this.supabase.client.functions.invoke(
          'pushNotificationsSubscription/api/subscribe',
          { body: { fcmToken, userID: user.id, clubID: c.club_id } }
        );
        if (error) throw error;
      }

      localStorage.setItem('actively_fcm_token', fcmToken);
      this._state.set('enabled');
    } catch (e) {
      console.error('push enable failed:', e);
      this._state.set('error');
    }
    return this._state();
  }
}
